
import { Component, InjectReactive, Prop, ProvideReactive, Vue, Watch } from 'vue-property-decorator';
import { Getter, Mutation } from 'vuex-class';

import BackSvg from '@/components/BackSvg.vue';
import LocationInfo from '@/components/LocationInfo.vue';
import Locations from '@/components/Locations.vue';
import TimeWanted from '@/components/TimeWanted.vue';
import { MutationTypes, OrderMethods, Views } from '@/enums';
import BasketModule from '@/store/modules/basket';
import UserModule from '@/store/modules/user';
import { parseDate } from '@/utils/global.util';

import ChooseOrderMethod from './ChooseOrderMethod.vue';

const { SET_ACTIVE_RESTAURANT, SET_EDIT_IS_OPEN, SET_PREVIEW_LOCATION, SET_RESTAURANTS } = MutationTypes;

@Component({
  components: {
    BackSvg,
    LocationInfo,
    Locations,
    'order-methods': ChooseOrderMethod,
    TimeWanted,
  }
})
export default class SelectLocationAndTimeDetails extends Vue {
  @Getter activeRestaurant!: Restaurant;
  @Getter previewLocation!: Restaurant | null;
  @Getter editIsOpen: boolean;
  @InjectReactive() viewContainerRef!: HTMLElement;
  @Mutation(`${SET_ACTIVE_RESTAURANT}`) setActiveRestaurant: (restaurant: Restaurant | null) => void;
  @Mutation(SET_PREVIEW_LOCATION) setPreviewLocation: (location: Restaurant | null) => void;
  @Mutation(SET_EDIT_IS_OPEN) setEditIsOpen: (action: boolean) => void;
  @Mutation(SET_RESTAURANTS) setRestaurants: (restaurants: Restaurant[]) => void;
  @Prop() containerRef;
  @ProvideReactive('currentOrderMethod') currentOrderMethod: OrderMethods | null;

  deliveryFormModel: DeliveryAddress;
  vehicleFormModel: CurbsideRequestModel;

  editOrderMethod: boolean;
  editLocation: boolean;
  editCustomerDetails: boolean;
  deliveryOrderMethodSelected: boolean;
  selectedDeliveryAddress: string;
  showNewAddressForm: boolean;
  storeOrderMethodSelected: boolean;
  timeWantedIncrement: number;

  data() {
    return {
      currentOrderMethod: null,
      deliveryFormModel: {},
      vehicleFormModel: {
        color: '',
        make: '',
        model: '',
      },
      editOrderMethod: false,
      editLocation: false,
      editCustomerDetails: false,
      deliveryOrderMethodSelected: false,
      selectedDeliveryAddress: '',
      showNewAddressForm: false,
      storeOrderMethodSelected: false,
    }
  }

  mounted() {
    if (this.currentBasket?.id !== undefined) {
      this.$emit('hide-quick-orders');
      this.setCurrentOrderMethodFromBasket();

      if (BasketModule.current?.deliveryAddress) {
        this.deliveryFormModel = {
          id: BasketModule.current.deliveryAddress.id,
          streetAddress: BasketModule.current.deliveryAddress.streetAddress,
          building: BasketModule.current.deliveryAddress.building,
          city: BasketModule.current.deliveryAddress.city,
          zipCode: BasketModule.current.deliveryAddress.zipCode,
          phoneNumber: BasketModule.current.deliveryAddress.phoneNumber,
          specialInstructions: BasketModule.current.deliveryAddress.specialInstructions,
          isDefault: BasketModule.current.deliveryAddress.isDefault
        }
      }
    } else {
      this.currentOrderMethod = null;
      this.$emit('show-quick-orders');
    }

    this.deliveryOrderMethodSelected = BasketModule.isDelivery;
    this.storeOrderMethodSelected = !BasketModule.isDelivery;
  }

  get userLocations() {
    return [...UserModule.favoriteRestaurants];
  }

  @Watch('userLocations')
  async handleUserKnonwLocations(newLocations, oldLocations) {
    if (newLocations?.length !== oldLocations?.length) {
      this.setRestaurants(newLocations);
    }
  }

  get currentBasket() {
    return BasketModule.current;
  }

  get isCheckoutPage() {
    return this.$route.name === Views.CHECKOUT;
  }

  @Watch('currentBasket')
  handleBasketChanged() {
    this.setCurrentOrderMethodFromBasket()
  }

  setCurrentOrderMethodFromBasket() {
    if (this.currentBasket) {
      switch (this.currentBasket.deliveryMode) {
        case OrderMethods.DISPATCH:
        case OrderMethods.DELIVERY:
          this.currentOrderMethod = OrderMethods.DELIVERY;
          break;
        case OrderMethods.PICKUP:
          this.currentOrderMethod = OrderMethods.PICKUP;
          break;
        case OrderMethods.CURBSIDE:
          this.currentOrderMethod = OrderMethods.CURBSIDE;
          break;
      }
    } else {
      this.currentOrderMethod = null;
    }
  }

  @Watch('currentOrderMethod')
  async watchCurrentOrderMethod(newValue, oldValue) {
    const loader = this.$loading.show();
    try {
      if (newValue === null) {
        this.$emit('show-quick-orders');
      } else {
        this.$emit('hide-quick-orders');
      }

      this.deliveryOrderMethodSelected = newValue === OrderMethods.DELIVERY;
      this.storeOrderMethodSelected = newValue !== OrderMethods.DELIVERY;

      if (this.editOrderMethod) {
        this.editOrderMethod = false;

        if (!this.activeRestaurant.supportsCurbside && newValue === OrderMethods.CURBSIDE) {
          return this.setActiveRestaurant({} as Restaurant);
        }

        if (!this.activeRestaurant.supportsPickup && newValue === OrderMethods.PICKUP) {
          return this.setActiveRestaurant({} as Restaurant);
        }

        const newMethod = newValue !== BasketModule.current?.deliveryMode;
        const notDelivery = [OrderMethods.PICKUP, OrderMethods.CURBSIDE].includes(newValue);
        const basketExists = BasketModule.id;
        if (newMethod && basketExists && notDelivery) {
          await BasketModule.setHandoffMethod({ method: newValue });
          if (newValue === OrderMethods.PICKUP) {
            this.$emit('handle-action', { action: 'toggleCollapse', data: false });
          }
        }
      } else {
        if (newValue !== oldValue && !BasketModule.id) {
          let basketWasCreated = false;
          if (UserModule.loggedIn && !this.activeRestaurant?.id) {
            const defaultDeliveryAddress = UserModule.defaultDeliveryAddress;
            const defaultRestaurant = UserModule.favoriteRestaurants.find((restaurant) => restaurant.isDefault);

            if (newValue === OrderMethods.PICKUP && defaultRestaurant?.supportsPickup) {
              await BasketModule.createPickupBasket({ restaurantId: defaultRestaurant.id });
              basketWasCreated = true;
            } else if (newValue === OrderMethods.CURBSIDE && defaultRestaurant?.supportsCurbside) {
              await BasketModule.createCurbsideBasket({ restaurantId: defaultRestaurant.id, requestModel: { make: '', model: '', colol: '' } });
              basketWasCreated = true;
            } else if (newValue === OrderMethods.DELIVERY && defaultDeliveryAddress) {
              await BasketModule.createDeliveryBasket({ deliveryAddress: defaultDeliveryAddress });
              basketWasCreated = true;
            }
          } else {
            if (newValue === OrderMethods.PICKUP && this.activeRestaurant.supportsPickup) {
              await BasketModule.createPickupBasket({ restaurantId: this.activeRestaurant.id });
              basketWasCreated = true;
            } else if (newValue === OrderMethods.CURBSIDE && this.activeRestaurant.supportsCurbside) {
              await BasketModule.createCurbsideBasket({ restaurantId: this.activeRestaurant.id, requestModel: { make: '', model: '', colol: '' } });
              basketWasCreated = true;
            }
          }

          if (basketWasCreated) {
            const location = this.$route.params?.location;
            if (this.activeRestaurant.slug && location !== this.activeRestaurant.slug) {
              this.$router.push({
                name: this.$route.name as string,
                params: {
                  ...this.$route.params,
                  location: this.activeRestaurant.slug,
                }
              });
            }
          }
        }
      }
    } catch (err) {
      this.$log.error(err);
      this.$toasted.error(err);
    } finally {
      loader.hide();
    }
  }

  get isLoggedIn() {
    return UserModule.loggedIn;
  }

  get isEditMode() {
    return this.editOrderMethod ||
      this.editLocation ||
      this.editCustomerDetails;
  }

  get showOrderMethodInput() {
    return this.editOrderMethod ||
      (BasketModule.id === '' && this.currentOrderMethod === null);
  }

  get showLocationContainer() {
    return (BasketModule.id !== '' && !this.editOrderMethod) ||
      (
        (this.currentOrderMethod === OrderMethods.PICKUP || this.currentOrderMethod === OrderMethods.CURBSIDE) &&
        !this.editOrderMethod
      );
  }

  get showStoreSelectionInput() {
    if (this.editLocation && (this.currentOrderMethod === OrderMethods.PICKUP || this.currentOrderMethod === OrderMethods.CURBSIDE)) {
      return true;
    }

    if (this.editOrderMethod ||
      this.editCustomerDetails ||
      this.currentOrderMethod === OrderMethods.DELIVERY ||
      this.currentOrderMethod === OrderMethods.DISPATCH) {
      return false;
    }

    if ((this.currentOrderMethod === OrderMethods.PICKUP || this.currentOrderMethod === OrderMethods.CURBSIDE) &&
      this.storeOrderMethodSelected &&
      !this.activeRestaurant.id) {
      return true;
    }
    return false;
  }

  get locationText() {
    if (this.currentOrderMethod === OrderMethods.PICKUP || this.currentOrderMethod === OrderMethods.CURBSIDE) {
      return this.activeRestaurant.name || '';
    }
    return this.currentBasket?.deliveryAddress?.streetAddress || '';
  }

  get locationLineTwoText() {
    if (this.currentOrderMethod === OrderMethods.PICKUP || this.currentOrderMethod === OrderMethods.CURBSIDE) {
      return this.activeRestaurant.street || '';
    }
    return `${this.currentBasket?.deliveryAddress?.city}, ${this.currentBasket?.deliveryAddress?.zipCode}` || '';
  }

  get showAddressSelectionInput() {
    if (this.editOrderMethod || this.editCustomerDetails) {
      return false;
    }

    return (this.currentOrderMethod === OrderMethods.DELIVERY &&
      this.deliveryOrderMethodSelected &&
      this.currentBasket?.deliveryAddress?.streetAddress === undefined) || (this.editLocation && this.currentOrderMethod === OrderMethods.DELIVERY);
  }

  get availableDeliveryAddresses() {
    return UserModule.savedDeliveryAddresses?.reduce((acc: any, { streetAddress, id }: DeliveryAddress) => {
      acc.push({ label: streetAddress, value: id });
      return acc;
    }, []) || null;
  }

  get showAddressInputForm() {
    return this.isChangingToDeliveryWithNoAddressesAvailable ||
      this.isEditingDeliveryAddressWithNoAddressesAvailable ||
      this.showNewAddressForm;
  }

  get isChangingToDeliveryWithNoAddressesAvailable(): boolean {
    return this.currentOrderMethod === OrderMethods.DELIVERY &&
      !this.availableDeliveryAddresses &&
      !this.currentBasket?.deliveryAddress;
  }

  get isEditingDeliveryAddressWithNoAddressesAvailable(): boolean {
    return this.editLocation && !this.availableDeliveryAddresses;
  }

  get showVehicleInformationContainer() {
    return (this.currentOrderMethod === OrderMethods.CURBSIDE && this.activeRestaurant.id && !this.isEditMode) || this.editCustomerDetails;
  }

  get showVehicleInformationInput() {
    return this.currentOrderMethod === OrderMethods.CURBSIDE && (this.editCustomerDetails || !BasketModule.curbsideDetailsExist);
  }

  get vehicleInformation() {
    return {
      make: (BasketModule?.current?.customFields?.find((field) => field.label === 'Make'))?.value || '',
      model: (BasketModule?.current?.customFields?.find((field) => field.label === 'Model'))?.value || '',
      color: (BasketModule?.current?.customFields?.find((field) => field.label === 'Color'))?.value || '',
    }
  }

  handleToggleOrderMethodClick() {
    this.editOrderMethod = !this.editOrderMethod;
  }

  handleToggleLocationClick() {
    this.editLocation = !this.editLocation;
  }

  handleToggleCustomerDetailsClick() {
    this.editCustomerDetails = !this.editCustomerDetails;
  }

  async handleSelectLocation(newLocation: Restaurant) {
    if (!newLocation) {
      this.$log.error('no newLocation', newLocation);
      return;
    }

    if (this.previewLocation) {
      this.setPreviewLocation(null);
    }

    const loader = this.$loading.show();
    this.editLocation = false;
    try {
      if (!BasketModule.id) {
        if (this.currentOrderMethod === OrderMethods.PICKUP) {
          await BasketModule.createPickupBasket({ restaurantId: newLocation.id });
        } else if (this.currentOrderMethod === OrderMethods.CURBSIDE) {
          await BasketModule.createCurbsideBasket({ restaurantId: newLocation.id, requestModel: { make: '', model: '', colol: '' } });
        }
      } else {
        if (BasketModule.restaurantId !== newLocation.id) {
          // Transfer nukes vehicle details, so capture before hand and re-apply for the user.
          const { make, model, color } = this.vehicleInformation;
          const timeWanted = this.currentBasket.timeWanted;

          await BasketModule.transferBasket({ restaurantId: newLocation.id });

          if (UserModule.loggedIn && this.isCheckoutPage) {
            UserModule.getBillingAccounts(BasketModule.id);
          }

          this.$router.push({
            name: this.$route.name as string,
            params: {
              ...this.$route.params,
              location: newLocation.slug,
            }
          });

          if (timeWanted) {
            const timeWantedDate = parseDate(timeWanted) as Date;
            const request: TimeWantedRequest = {
              year: timeWantedDate.getFullYear(),
              month: timeWantedDate.getMonth() + 1,
              day: timeWantedDate.getDate(),
              hour: timeWantedDate.getHours(),
              minute: timeWantedDate.getMinutes(),
            };
            await BasketModule.setTimeWanted(request);
          }

          if (this.currentBasket.deliveryMode === OrderMethods.CURBSIDE && make) {
            await BasketModule.setCurbsideDetails({ make, model, color });
          }
          this.setCurrentOrderMethodFromBasket();
        }
      }
      this.$emit('handle-action', { action: 'toggleCollapse', data: false });
    } catch (err) {
      this.$toasted.error(err.data)
      this.$log.error(err);
    } finally {
      loader.hide();
    }
  }

  async submitSelectSavedAddress() {
    const loader = this.$loading.show();
    try {
      const savedAddress = UserModule.savedDeliveryAddresses.find((deliveryAddress) => deliveryAddress.id === parseInt(this.selectedDeliveryAddress));
      const { id, building, streetAddress, city, zipCode } = savedAddress || {};
      const deliveryAddress = { id, Building: building, StreetAddress: streetAddress, City: city, ZipCode: zipCode };
      if (!BasketModule.id) {
        await BasketModule.createDeliveryBasket({ deliveryAddress });
      } else {
        await BasketModule.updateBasketToDelivery({ basketId: BasketModule.id, deliveryAddress });

        if (UserModule.loggedIn && this.isCheckoutPage) {
          UserModule.getBillingAccounts(BasketModule.id);
        }
      }

      const location = this.$route.params?.location;
      if (this.activeRestaurant.slug && location !== this.activeRestaurant.slug) {
        this.$router.push({
          name: this.$route.name as string,
          params: {
            ...this.$route.params,
            location: this.activeRestaurant.slug,
          }
        });
      }

      this.$emit('handle-action', { action: 'toggleCollapse', data: false });
    } catch (err) {
      this.$log.error(err);
      this.$toasted.error(err.data);
    }
    loader.hide();
  }

  async submitFormData(data) {
    const loader = this.$loading.show();
    try {
      if (this.currentOrderMethod === OrderMethods.CURBSIDE) {
        const { make, model, color } = data;
        if (!BasketModule.id) {
          await BasketModule.createCurbsideBasket({ restaurantId: this.activeRestaurant.id, requestModel: { make, model, color } });
        } else {
          if (BasketModule.restaurantId !== this.activeRestaurant.id) {
            const timeWanted = this.currentBasket.timeWanted;

            await BasketModule.transferBasket({ restaurantId: this.activeRestaurant.id });

            if (UserModule.loggedIn && this.isCheckoutPage) {
              UserModule.getBillingAccounts(BasketModule.id);
            }

            this.$router.push({
              name: this.$route.name as string,
              params: {
                ...this.$route.params,
                location: this.activeRestaurant.slug,
              }
            });

            if (timeWanted) {
              const timeWantedDate = parseDate(timeWanted) as Date;
              const request: TimeWantedRequest = {
                year: timeWantedDate.getFullYear(),
                month: timeWantedDate.getMonth() + 1,
                day: timeWantedDate.getDate(),
                hour: timeWantedDate.getHours(),
                minute: timeWantedDate.getMinutes(),
              };
              await BasketModule.setTimeWanted(request);
            }
          }
          await BasketModule.setCurbsideDetails(data);
          this.editCustomerDetails = false;
        }
      } else if (this.currentOrderMethod === OrderMethods.DELIVERY) {
        const { building, streetAddress, city, zipCode, phoneNumber, specialInstructions, isDefault } = data;
        const deliveryAddress = { Building: building, StreetAddress: streetAddress, City: city, ZipCode: zipCode, PhoneNumber: phoneNumber, SpecialInstructions: specialInstructions, IsDefault: isDefault };
        if (!BasketModule.id) {
          await BasketModule.createDeliveryBasket({ deliveryAddress });
        } else {
          await BasketModule.updateBasketToDelivery({ basketId: BasketModule.id, deliveryAddress });
        }

        const location = this.$route.params?.location;
        if (this.activeRestaurant.slug && location !== this.activeRestaurant.slug) {
          this.$router.push({
            name: this.$route.name as string,
            params: {
              ...this.$route.params,
              location: this.activeRestaurant.slug,
            }
          });

          if (UserModule.loggedIn && this.isCheckoutPage) {
            UserModule.getBillingAccounts(BasketModule.id);
          }
        }
      }

      this.$emit('handle-action', { action: 'toggleCollapse', data: false });
    } catch (err) {
      this.$log.error(err);
      this.$toasted.error(err.data);
    }
    loader.hide();
  }

  get curbsideDetailsExist() {
    return BasketModule.curbsideDetailsExist;
  }

  async handleOnSave() {
    if (this.$route.name === Views.SELECT_LOCATION) {
      if (this.activeRestaurant?.slug) {
        this.$router.push({
          name: 'ChooseFood',
          params: {
            ...this.$route.params,
            location: this.activeRestaurant.slug,
          },
        });
      } else {
        this.$log.error('No activeRestaurant.slug found');
      }
    } else {
      this.setEditIsOpen(false);
    }
  }

  get headingText() {
    if (!this.currentOrderMethod || this.editOrderMethod) {
      return 'What type of order can we get started for you?'
    }

    if (this.currentOrderMethod === OrderMethods.PICKUP && !this.activeRestaurant?.id) {
      return 'Start a pickup order';
    }

    if (this.currentOrderMethod === OrderMethods.CURBSIDE && (!this.activeRestaurant?.id || this.vehicleInformation.make === '')) {
      return 'Start a curbside order';
    }

    if (this.currentOrderMethod === OrderMethods.DELIVERY && !this.activeRestaurant?.id) {
      return 'Start a delivery order';
    }
  }

  get headingSubText() {
    if (!this.currentOrderMethod || this.editOrderMethod) {
      return 'Order ahead for pickup or let us deliver to your location.';
    }

    if (this.currentOrderMethod === OrderMethods.CURBSIDE && this.vehicleInformation.make === '' && this.activeRestaurant?.id && !this.editLocation) {
      return 'Enter your vehicle details.'
    }

    if ((this.currentOrderMethod === OrderMethods.PICKUP || this.currentOrderMethod === OrderMethods.CURBSIDE) && (!this.activeRestaurant?.id || this.editLocation)) {
      return 'Find a store near your location.';
    }

    if (this.currentOrderMethod === OrderMethods.DELIVERY && !this.activeRestaurant?.id) {
      return 'Enter your address to find a store that delivers to you.';
    }
  }
}
